import { useRef } from "react";
import { Button, Divider, Flex, Input, Tooltip } from "antd";

import BoldIcon from "@/icons/BoldIcon";
import ItalicIcon from "@/icons/ItalicIcon";
import BulletListIcon from "@/icons/BulletListIcon";
import NumberListIcon from "@/icons/NumberListIcon";
import LinkIcon from "@/icons/LinkIcon";
import UnderlineIcon from "@/icons/UnderlineIcon";
import StrikethroughIcon from "@/icons/StrikethroughIcon";
import AnnotationIcon from "@/icons/AnnotationIcon";
import TableIcon from "@/icons/TableIcon";
import CodeIcon from "@/icons/CodeIcon";
import HighlightIcon from "@/icons/HighlightIcon";

import { EMOJI_SHORTCODES } from "./emojiShortcodes";

const HTML_TAG_REPLACEMENTS = [
  [/<br\s*\/?>/gi, "\n"],
  [/<\/p\s*>/gi, "\n\n"],
  [/<p[^>]*>/gi, ""],
  [/<\/div\s*>/gi, "\n"],
  [/<div[^>]*>/gi, ""],
  [/<\/h1\s*>/gi, "\n\n"],
  [/<h1[^>]*>/gi, "# "],
  [/<\/h2\s*>/gi, "\n\n"],
  [/<h2[^>]*>/gi, "## "],
  [/<\/h3\s*>/gi, "\n\n"],
  [/<h3[^>]*>/gi, "### "],
  [/<\/h[4-6]\s*>/gi, "\n\n"],
  [/<h[4-6][^>]*>/gi, "#### "],
  [/<strong[^>]*>/gi, "**"],
  [/<\/strong\s*>/gi, "**"],
  [/<b[^>]*>/gi, "**"],
  [/<\/b\s*>/gi, "**"],
  [/<em[^>]*>/gi, "_"],
  [/<\/em\s*>/gi, "_"],
  [/<i[^>]*>/gi, "_"],
  [/<\/i\s*>/gi, "_"],
  [/<u[^>]*>/gi, "++"],
  [/<\/u\s*>/gi, "++"],
  [/<(s|del|strike)[^>]*>/gi, "~~"],
  [/<\/(s|del|strike)\s*>/gi, "~~"],
  [/<code[^>]*>/gi, "`"],
  [/<\/code\s*>/gi, "`"],
  [/<mark[^>]*>/gi, "=="],
  [/<\/mark\s*>/gi, "=="],
  [/<blockquote[^>]*>/gi, "> "],
  [/<\/blockquote\s*>/gi, "\n"],
  [/<li[^>]*>/gi, "- "],
  [/<\/li\s*>/gi, "\n"],
  [/<\/?(ul|ol)[^>]*>/gi, "\n"],
  [/<hr\s*\/?>/gi, "\n---\n"],
];

const hasHtmlTags = (value) => /<\/?[a-z][\s\S]*>/i.test(value);

export const normalizeDescriptionMarkdown = (value = "") => {
  if (!value) return "";
  if (!hasHtmlTags(value)) return value;

  const valueWithLinks = value.replace(
    /<a[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi,
    (_, href, text) => `[${text}](${href})`,
  );

  return HTML_TAG_REPLACEMENTS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    valueWithLinks,
  )
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const isSafeUrl = (url = "") => {
  const trimmedUrl = url.trim();
  return /^(https?:\/\/|mailto:|tel:|\/|#|blob:|data:image\/)/i.test(trimmedUrl);
};

const renderTextWithEmoji = (text, keyPrefix) => {
  const parts = [];
  const pattern = /:([a-z0-9_+-]+):/gi;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const emoji = EMOJI_SHORTCODES[match[1].toLowerCase()];
    parts.push(emoji || match[0]);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : [text];
};

const stripInlineMarkdown = (text = "") =>
  text
    .replace(/\[[^\]]+\]\(([^)]+)\)/g, "$1")
    .replace(/(`|\*\*|__|\+\+|~~|==|\*|_)/g, "")
    .replace(/:([a-z0-9_+-]+):/gi, "$1");

const slugifyHeading = (text = "") =>
  stripInlineMarkdown(text)
    .toLowerCase()
    .trim()
    .replace(/[^\wа-яё\s-]/gi, "")
    .replace(/_/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const renderInline = (text, keyPrefix) => {
  const parts = [];
  const pattern = /(`[^`\n]+`|!\[[^\]]*\]\([^)]+\)|\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|__[^_]+__|\+\+[^+]+\+\+|~~[^~]+~~|==[^=]+==|\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(...renderTextWithEmoji(text.slice(lastIndex, match.index), `${keyPrefix}-${lastIndex}`));
    }

    const token = match[0];
    const key = `${keyPrefix}-${match.index}`;

    if (token.startsWith("`")) {
      const content = token.replace(/^`/, "").replace(/`$/, "");
      parts.push(<code key={key}>{content}</code>);
    } else if (token.startsWith("![")) {
      const imageMatch = /^!\[([^\]]*)\]\(([^)]+)\)$/.exec(token);
      const src = imageMatch?.[2]?.trim();

      if (imageMatch && isSafeUrl(src)) {
        parts.push(
          <img
            key={key}
            src={src}
            alt={imageMatch[1]}
            className="markdown-image"
          />,
        );
      } else {
        parts.push(token);
      }
    } else if (token.startsWith("[")) {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      const href = linkMatch?.[2]?.trim();

      if (linkMatch && isSafeUrl(href)) {
        const isAnchor = href.startsWith("#");
        parts.push(
          <a
            key={key}
            href={href}
            target={isAnchor ? undefined : "_blank"}
            rel={isAnchor ? undefined : "noreferrer"}
          >
            {renderInline(linkMatch[1], `${key}-link`)}
          </a>,
        );
      } else {
        parts.push(token);
      }
    } else if (token.startsWith("**") || token.startsWith("__")) {
      const content = token.replace(/^(\*\*|__)/, "").replace(/(\*\*|__)$/, "");
      parts.push(<strong key={key}>{renderInline(content, `${key}-strong`)}</strong>);
    } else if (token.startsWith("++")) {
      const content = token.replace(/^\+\+/, "").replace(/\+\+$/, "");
      parts.push(<u key={key}>{renderInline(content, `${key}-underline`)}</u>);
    } else if (token.startsWith("~~")) {
      const content = token.replace(/^~~/, "").replace(/~~$/, "");
      parts.push(<s key={key}>{renderInline(content, `${key}-strike`)}</s>);
    } else if (token.startsWith("==")) {
      const content = token.replace(/^==/, "").replace(/==$/, "");
      parts.push(<mark key={key}>{renderInline(content, `${key}-mark`)}</mark>);
    } else {
      const content = token.replace(/^(\*|_)/, "").replace(/(\*|_)$/, "");
      parts.push(<em key={key}>{renderInline(content, `${key}-em`)}</em>);
    }

    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    parts.push(...renderTextWithEmoji(text.slice(lastIndex), `${keyPrefix}-${lastIndex}`));
  }

  return parts.length ? parts : text;
};

const renderParagraph = (text, key) => (
  <p key={key}>
    {text.split("\n").map((line, index) => (
      <span key={`${key}-${index}`}>
        {index > 0 && <br />}
        {renderInline(line, `${key}-${index}`)}
      </span>
    ))}
  </p>
);

const renderList = (items, ordered, key) => {
  const Tag = ordered ? "ol" : "ul";

  return (
    <Tag key={key}>
      {items.map((item, index) => (
        <li key={`${key}-${index}`}>{renderInline(item, `${key}-${index}`)}</li>
      ))}
    </Tag>
  );
};

const parseTableRow = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const isTableDivider = (line) => {
  const cells = parseTableRow(line);
  return cells.length > 1 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

const renderTable = (header, rows, key) => (
  <div key={key} className="markdown-table-wrap">
    <table>
      <thead>
        <tr>
          {header.map((cell, index) => (
            <th key={`${key}-head-${index}`}>{renderInline(cell, `${key}-head-${index}`)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={`${key}-row-${rowIndex}`}>
            {header.map((_, cellIndex) => (
              <td key={`${key}-cell-${rowIndex}-${cellIndex}`}>
                {renderInline(row[cellIndex] || "", `${key}-cell-${rowIndex}-${cellIndex}`)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const renderQuote = (lines, key) => {
  const content = lines.map((line) => line.replace(/^>\s?/, "")).join("\n");

  return <blockquote key={key}>{renderMarkdownBlocks(content)}</blockquote>;
};

const renderMarkdownBlocks = (value) => {
  const markdown = normalizeDescriptionMarkdown(value);
  if (!markdown) return null;

  const nodes = [];
  const lines = markdown.split(/\r?\n/);
  let paragraph = [];
  let list = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    nodes.push(renderParagraph(paragraph.join("\n"), `p-${nodes.length}`));
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;
    nodes.push(renderList(list.items, list.ordered, `list-${nodes.length}`));
    list = null;
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    const unorderedListItem = /^\s*[-*]\s+(.+)$/.exec(line);
    const orderedListItem = /^\s*\d+[.)]\s+(.+)$/.exec(line);

    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }

    if (line.includes("|") && isTableDivider(lines[index + 1] || "")) {
      const header = parseTableRow(line);
      const rows = [];
      index += 2;

      while (index < lines.length && lines[index].includes("|") && lines[index].trim()) {
        rows.push(parseTableRow(lines[index]));
        index += 1;
      }

      index -= 1;
      flushParagraph();
      flushList();
      nodes.push(renderTable(header, rows, `table-${nodes.length}`));
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quoteLines = [];

      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quoteLines.push(lines[index]);
        index += 1;
      }

      index -= 1;
      flushParagraph();
      flushList();
      nodes.push(renderQuote(quoteLines, `quote-${nodes.length}`));
      continue;
    }

    if (/^\s*---+\s*$/.test(line)) {
      flushParagraph();
      flushList();
      nodes.push(<hr key={`hr-${nodes.length}`} />);
      continue;
    }

    if (heading) {
      const HeadingTag = `h${heading[1].length}`;
      const headingId = slugifyHeading(heading[2]);
      flushParagraph();
      flushList();
      nodes.push(
        <HeadingTag key={`h-${nodes.length}`} id={headingId || undefined}>
          {renderInline(heading[2], `h-${nodes.length}`)}
        </HeadingTag>,
      );
      continue;
    }

    if (unorderedListItem || orderedListItem) {
      const ordered = Boolean(orderedListItem);
      const item = unorderedListItem?.[1] || orderedListItem?.[1];
      flushParagraph();

      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }

      list.items.push(item);
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();

  return nodes;
};

const insertMarkdown = (value, selectionStart, selectionEnd, before, after = before) => {
  const selected = value.slice(selectionStart, selectionEnd);
  const text = selected || "text";
  const next = `${value.slice(0, selectionStart)}${before}${text}${after}${value.slice(selectionEnd)}`;
  const cursorStart = selectionStart + before.length;
  const cursorEnd = cursorStart + text.length;

  return { next, cursorStart, cursorEnd };
};

const insertLink = (value, selectionStart, selectionEnd) => {
  const selected = value.slice(selectionStart, selectionEnd) || "link text";
  const before = "[";
  const middle = "](https://)";
  const next = `${value.slice(0, selectionStart)}${before}${selected}${middle}${value.slice(selectionEnd)}`;
  const cursorStart = selectionStart + before.length + selected.length + 2;
  const cursorEnd = cursorStart + "https://".length;

  return { next, cursorStart, cursorEnd };
};

const insertBlock = (value, selectionStart, selectionEnd, block) => {
  const selected = value.slice(selectionStart, selectionEnd);
  const blockText = selected || block;
  const prefix = selectionStart > 0 && value[selectionStart - 1] !== "\n" ? "\n\n" : "";
  const suffix = selectionEnd < value.length && value[selectionEnd] !== "\n" ? "\n\n" : "";
  const next = `${value.slice(0, selectionStart)}${prefix}${blockText}${suffix}${value.slice(selectionEnd)}`;
  const cursorStart = selectionStart + prefix.length;
  const cursorEnd = cursorStart + blockText.length;

  return { next, cursorStart, cursorEnd };
};

const insertTable = (value, selectionStart, selectionEnd) =>
  insertBlock(
    value,
    selectionStart,
    selectionEnd,
    "| Столбец 1 | Столбец 2 | Столбец 3 |\n|-----------|-----------|-----------|\n| Строка 1 | Строка 1 | Строка 1 |\n| Строка 2 | Строка 2 | Строка 2 |",
  );

const clearLineMarkup = (line) =>
  line.replace(/^\s*#{1,6}\s+/, "").replace(/^\s*(?:[-*]|\d+[.)])\s+/, "");

const applyLinePrefix = (value, selectionStart, selectionEnd, prefix) => {
  const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
  const lineEnd = selectionEnd < value.length ? value.indexOf("\n", selectionEnd) : -1;
  const end = lineEnd === -1 ? value.length : lineEnd;
  const selected = value.slice(lineStart, end) || "";
  const lines = selected.split("\n");
  const nextSelected = lines
    .map((line, index) => {
      if (!line.trim()) return line;

      const cleanLine = clearLineMarkup(line);
      return typeof prefix === "function" ? prefix(cleanLine, index) : `${prefix}${cleanLine}`;
    })
    .join("\n");

  return {
    next: `${value.slice(0, lineStart)}${nextSelected}${value.slice(end)}`,
    cursorStart: lineStart,
    cursorEnd: lineStart + nextSelected.length,
  };
};

const applyQuotePrefix = (value, selectionStart, selectionEnd) => {
  const lineStart = value.lastIndexOf("\n", selectionStart - 1) + 1;
  const lineEnd = selectionEnd < value.length ? value.indexOf("\n", selectionEnd) : -1;
  const end = lineEnd === -1 ? value.length : lineEnd;
  const selected = value.slice(lineStart, end) || "Цитата";
  const nextSelected = selected
    .split("\n")
    .map((line) => (line.startsWith(">") ? line : `> ${line}`))
    .join("\n");

  return {
    next: `${value.slice(0, lineStart)}${nextSelected}${value.slice(end)}`,
    cursorStart: lineStart,
    cursorEnd: lineStart + nextSelected.length,
  };
};

export const MarkdownText = ({ value = "", className = "", empty = null }) => {
  const content = renderMarkdownBlocks(value);

  if (!content) return empty;

  return <div className={`markdown-text ${className}`.trim()}>{content}</div>;
};

export const MarkdownEditor = ({
  value = "",
  onChange,
  disabled,
  rows = 5,
  autoFocus,
  extraToolbar = null,
  onPaste,
  onDrop,
  onDragOver,
}) => {
  const inputRef = useRef(null);
  const normalizedValue = normalizeDescriptionMarkdown(value);

  const updateValue = ({ next, cursorStart, cursorEnd }, scrollTop = 0) => {
    onChange?.(next);

    window.requestAnimationFrame(() => {
      const textarea = inputRef.current?.resizableTextArea?.textArea;
      textarea?.focus();
      textarea?.setSelectionRange(cursorStart, cursorEnd);
      if (textarea) {
        textarea.scrollTop = scrollTop;
      }
    });
  };

  const applyFormat = (format) => {
    const textarea = inputRef.current?.resizableTextArea?.textArea;
    if (!textarea) return;

    const { selectionStart, selectionEnd, scrollTop } = textarea;

    if (format === "paragraph") {
      updateValue(
        applyLinePrefix(normalizedValue, selectionStart, selectionEnd, ""),
        scrollTop,
      );
      return;
    }

    if (format === "heading") {
      updateValue(
        applyLinePrefix(normalizedValue, selectionStart, selectionEnd, "## "),
        scrollTop,
      );
      return;
    }

    if (format === "bold") {
      updateValue(
        insertMarkdown(normalizedValue, selectionStart, selectionEnd, "**"),
        scrollTop,
      );
      return;
    }

    if (format === "italic") {
      updateValue(
        insertMarkdown(normalizedValue, selectionStart, selectionEnd, "_"),
        scrollTop,
      );
      return;
    }

    if (format === "underline") {
      updateValue(
        insertMarkdown(normalizedValue, selectionStart, selectionEnd, "++"),
        scrollTop,
      );
      return;
    }

    if (format === "strikethrough") {
      updateValue(
        insertMarkdown(normalizedValue, selectionStart, selectionEnd, "~~"),
        scrollTop,
      );
      return;
    }

    if (format === "code") {
      updateValue(
        insertMarkdown(normalizedValue, selectionStart, selectionEnd, "`"),
        scrollTop,
      );
      return;
    }

    if (format === "highlight") {
      updateValue(
        insertMarkdown(normalizedValue, selectionStart, selectionEnd, "=="),
        scrollTop,
      );
      return;
    }

    if (format === "link") {
      updateValue(insertLink(normalizedValue, selectionStart, selectionEnd), scrollTop);
      return;
    }

    if (format === "bullet") {
      updateValue(
        applyLinePrefix(normalizedValue, selectionStart, selectionEnd, "- "),
        scrollTop,
      );
      return;
    }

    if (format === "ordered") {
      updateValue(
        applyLinePrefix(
          normalizedValue,
          selectionStart,
          selectionEnd,
          (line, index) => `${index + 1}. ${line}`,
        ),
        scrollTop,
      );
      return;
    }

    if (format === "quote") {
      updateValue(
        applyQuotePrefix(normalizedValue, selectionStart, selectionEnd),
        scrollTop,
      );
      return;
    }

    if (format === "table") {
      updateValue(insertTable(normalizedValue, selectionStart, selectionEnd), scrollTop);
    }
  };

  return (
    <div className={`markdown-editor${disabled ? " disabled" : ""}`}>
      <Flex
        gap={5}
        wrap="wrap"
        align="center"
        className="markdown-editor-toolbar"
        onMouseDown={(event) => event.preventDefault()}
      >
        <Tooltip title="Текст">
          <Button size="small" onClick={() => applyFormat("paragraph")} disabled={disabled}>
            T
          </Button>
        </Tooltip>
        <Tooltip title="Заголовок">
          <Button size="small" onClick={() => applyFormat("heading")} disabled={disabled}>
            H
          </Button>
        </Tooltip>
        <Divider
          orientation="vertical"
          style={{ borderColor: "var(--color-gray)", marginInline: 5 }}
          size="small"
        />
        <Tooltip title="Жирный">
          <Button size="small" onClick={() => applyFormat("bold")} disabled={disabled}>
            <BoldIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Курсив">
          <Button size="small" onClick={() => applyFormat("italic")} disabled={disabled}>
            <ItalicIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Подчеркнутый">
          <Button size="small" onClick={() => applyFormat("underline")} disabled={disabled}>
            <UnderlineIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Зачеркнутый">
          <Button size="small" onClick={() => applyFormat("strikethrough")} disabled={disabled}>
            <StrikethroughIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Строчный код">
          <Button size="small" onClick={() => applyFormat("code")} disabled={disabled}>
            <CodeIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Маркер">
          <Button size="small" onClick={() => applyFormat("highlight")} disabled={disabled}>
            <HighlightIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Ссылка">
          <Button size="small" onClick={() => applyFormat("link")} disabled={disabled}>
            <LinkIcon />
          </Button>
        </Tooltip>
        <Divider
          orientation="vertical"
          style={{ borderColor: "var(--color-gray)", marginInline: 5 }}
          size="small"
        />
        <Tooltip title="Маркированный список">
          <Button size="small" onClick={() => applyFormat("bullet")} disabled={disabled}>
            <BulletListIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Нумерованный список">
          <Button size="small" onClick={() => applyFormat("ordered")} disabled={disabled}>
            <NumberListIcon />
          </Button>
        </Tooltip>
        <Divider
          orientation="vertical"
          style={{ borderColor: "var(--color-gray)", marginInline: 5 }}
          size="small"
        />
        <Tooltip title="Цитата">
          <Button size="small" onClick={() => applyFormat("quote")} disabled={disabled}>
            <AnnotationIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Таблица">
          <Button size="small" onClick={() => applyFormat("table")} disabled={disabled}>
            <TableIcon />
          </Button>
        </Tooltip>
        {extraToolbar}
      </Flex>

      <Input.TextArea
        ref={inputRef}
        value={normalizedValue}
        onChange={(event) => onChange?.(event.target.value)}
        rows={rows}
        autoSize={{ minRows: rows, maxRows: 14 }}
        disabled={disabled}
        autoFocus={autoFocus}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={onDragOver}
        className="markdown-editor-input"
      />
    </div>
  );
};

export default MarkdownText;
